// The signed-in frame: accent bar + header with the context switcher, the navigation of the
// current context, and the feedback button (mounted once, RULES.md section 7).
// One navigation list serves both screens: a sidebar from 768 px, and below that a bottom tab bar
// (the primary items) whose "More" tab opens a sheet with the rest and the account.
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router';
import {
  Activity,
  CalendarDays,
  ChevronDown,
  Ellipsis,
  FolderKanban,
  House,
  Lightbulb,
  LogOut,
  MessageCircle,
  Scale,
  Settings,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { FeedbackButton } from './FeedbackButton';
import { Modal } from './ui';

export const PERSONAL_ACCENT = '#475569';

interface NavItem {
  path: string;
  label: string;
  /** the tab-bar label, when the sidebar's is too long for a phone */
  short?: string;
  icon: LucideIcon;
  /** shown in the phone's tab bar; the others are behind "More" */
  primary?: boolean;
}

const WORKSPACE_NAV: NavItem[] = [
  { path: 'desk', label: 'Desk', icon: House, primary: true },
  { path: 'assistant', label: 'Assistant', icon: MessageCircle, primary: true },
  { path: 'projects', label: 'Projects', icon: FolderKanban, primary: true },
  { path: 'ideas', label: 'Ideas', icon: Lightbulb },
  { path: 'meetings', label: 'Meetings', icon: CalendarDays },
  { path: 'decisions', label: 'Decisions', icon: Scale, primary: true },
  { path: 'team', label: 'Team', icon: Users },
  { path: 'activity', label: 'Activity', icon: Activity },
  { path: 'settings', label: 'Settings', icon: Settings },
];

const PERSONAL_NAV: NavItem[] = [
  { path: '/', label: 'My desk', short: 'Desk', icon: House, primary: true },
  { path: '/assistant', label: 'Personal assistant', short: 'Assistant', icon: MessageCircle, primary: true },
  { path: '/activity', label: 'My activity', short: 'Activity', icon: Activity, primary: true },
  { path: '/ideas', label: 'My ideas', short: 'Ideas', icon: Lightbulb },
  { path: '/me', label: 'My settings', icon: UserRound },
];

export function useContextWorkspace() {
  const m = useMatch('/w/:slug/*');
  const { workspace } = useSession();
  return workspace(m?.params.slug);
}

export function Layout() {
  const { me } = useSession();
  const ws = useContextWorkspace();
  const m = useMatch('/w/:slug/*');
  const location = useLocation();
  const [more, setMore] = useState(false);
  const accent = ws?.accent ?? PERSONAL_ACCENT;
  const style = { '--accent': accent } as CSSProperties;
  const items = ws ? WORKSPACE_NAV : PERSONAL_NAV;
  const href = (it: NavItem) => (ws ? `/w/${ws.slug}/${it.path}` : it.path);
  // Inside a conversation the phone gives the composer the bottom edge (no tab bar), like a chat app.
  const inThread = /^(\/w\/[^/]+)?\/assistant\/[^/]+/.test(location.pathname);

  useEffect(() => {
    document.title = ws ? `${ws.name} · Cognibrain` : 'Personal · Cognibrain';
  }, [ws]);

  useEffect(() => {
    setMore(false);
  }, [location.pathname]);

  return (
    <div className={`shell${inThread ? ' in-thread' : ''}`} style={style}>
      <div className="accent-bar" aria-hidden="true" />
      <header className="topbar">
        <span className="brand">Cognibrain</span>
        <Switcher />
        <div className="grow" />
        <span className="who" title={me.user.email}>
          {me.user.name}
        </span>
        <SignOut className="btn ghost small topbar-signout" />
      </header>
      <div className="body">
        <nav className="sidenav" aria-label={ws ? `${ws.name} navigation` : 'Personal navigation'}>
          <div className="context-name">
            <span className="dot" style={{ background: accent }} aria-hidden="true" />
            {ws ? ws.name : 'Personal'}
            {ws && <small className="muted">{ws.kind === 'joint' ? 'shared by both founders' : `you are ${ws.role}`}</small>}
            {!ws && <small className="muted">private to you</small>}
          </div>
          {items.map((it) => (
            <NavLink key={it.path} to={href(it)} end={it.path === '/'} className={it.primary ? 'nav-primary' : 'nav-secondary'}>
              <span className="nav-icon" aria-hidden="true">
                <it.icon size={20} strokeWidth={1.75} aria-hidden="true" />
              </span>
              {it.short ? (
                <>
                  <span className="label-full">{it.label}</span>
                  <span className="label-short">{it.short}</span>
                </>
              ) : (
                <span>{it.label}</span>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            className={`nav-more${more || items.some((it) => !it.primary && location.pathname.startsWith(href(it))) ? ' active' : ''}`}
            aria-haspopup="dialog"
            aria-expanded={more}
            onClick={() => setMore(true)}
          >
            <span className="nav-icon" aria-hidden="true">
              <Ellipsis size={20} strokeWidth={1.75} aria-hidden="true" />
            </span>
            <span>More</span>
          </button>
        </nav>
        <main id="main">
          {m && !ws ? (
            <div className="state error" role="alert">
              <strong>No such workspace, or you are not a member of it.</strong> {location.pathname}
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      {more && (
        <Modal title={ws ? ws.name : 'Personal'} onClose={() => setMore(false)}>
          <ul className="sheet-list">
            {items
              .filter((it) => !it.primary)
              .map((it) => (
                <li key={it.path}>
                  <NavLink to={href(it)} end={it.path === '/'}>
                    <it.icon size={20} strokeWidth={1.75} aria-hidden="true" />
                    <span>{it.label}</span>
                  </NavLink>
                </li>
              ))}
          </ul>
          <div className="sheet-account">
            <div>
              <strong>{me.user.name}</strong>
              <div className="muted small">{me.user.email}</div>
            </div>
            <SignOut className="btn" />
          </div>
        </Modal>
      )}
      <FeedbackButton
        user={{ userId: me.user.id, workspace: ws?.slug ?? 'personal' }}
        routeFileHint={() => routeFile(location.pathname)}
      />
    </div>
  );
}

function routeFile(path: string): string {
  const part = path.split('/')[3] ?? '';
  const pages: Record<string, string> = {
    desk: 'Desk.tsx',
    assistant: 'Assistant.tsx',
    projects: 'Projects.tsx / ProjectRoom.tsx',
    meetings: 'Meetings.tsx',
    decisions: 'Decisions.tsx',
    team: 'Team.tsx',
    ideas: 'Ideas.tsx',
    activity: 'Activity.tsx',
    settings: 'Settings.tsx',
  };
  return `web/src/pages/${path.startsWith('/w/') ? pages[part] ?? 'App.tsx' : 'Desk.tsx / Assistant.tsx / Me.tsx'}`;
}

function Switcher() {
  const { me } = useSession();
  const ws = useContextWorkspace();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div className="switcher" ref={box}>
      <button type="button" className="switcher-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className="dot" style={{ background: ws?.accent ?? PERSONAL_ACCENT }} aria-hidden="true" />
        <span className="switcher-name">{ws ? ws.name : 'Personal'}</span>
        <ChevronDown size={16} strokeWidth={2} aria-hidden="true" />
      </button>
      {open && (
        <>
          {/* on a phone the menu is a bottom sheet over this scrim; on a desktop the scrim is not drawn */}
          <div className="menu-scrim" aria-hidden="true" onClick={() => setOpen(false)} />
          <div className="menu" role="menu" aria-label="Switch workspace">
            <button type="button" role="menuitem" onClick={() => go('/')} className={!ws ? 'current' : ''}>
              <span className="dot" style={{ background: PERSONAL_ACCENT }} aria-hidden="true" />
              <span>
                Personal
                <small>only you</small>
              </span>
            </button>
            {me.workspaces.map((w) => (
              <button key={w.slug} type="button" role="menuitem" onClick={() => go(`/w/${w.slug}/desk`)} className={ws?.slug === w.slug ? 'current' : ''}>
                <span className="dot" style={{ background: w.accent }} aria-hidden="true" />
                <span>
                  {w.name}
                  <small>{w.kind === 'joint' ? 'joint · both founders' : `company · ${w.role}`}</small>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SignOut({ className }: { className: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className={className}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const r = await api<{ redirect: string }>('/api/auth/logout', { method: 'POST' });
          window.location.assign(r.redirect || '/login');
        } catch {
          window.location.assign('/login');
        }
      }}
    >
      <LogOut size={18} strokeWidth={1.75} aria-hidden="true" />
      Sign out
    </button>
  );
}
